import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Activity, Target, CheckCircle2, TrendingUp } from "lucide-react";
import { ResponsiveContainer, ComposedChart, Bar as ReBar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";

const isoToday = () => new Date().toISOString().slice(0, 10);
const isoDaysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

const kpiTone = (kpi) => {
  if (kpi === null || kpi === undefined) return "bg-slate-100 text-slate-500 border-slate-200";
  if (kpi >= 80) return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (kpi >= 50) return "bg-amber-50 text-amber-700 border-amber-200";
  return "bg-red-50 text-red-700 border-red-200";
};

export default function PerformancePage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [technicians, setTechnicians] = useState([]);
  const [technicianId, setTechnicianId] = useState("");
  const [dateFrom, setDateFrom] = useState(isoDaysAgo(29));
  const [dateTo, setDateTo] = useState(isoToday());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user || user.role === "tecnico") return;
    api.get("/users", { params: { role: "tecnico,subappalto" } }).then((r) => {
      setTechnicians(r.data);
      if (r.data.length > 0) setTechnicianId(r.data[0].id);
    }).catch(() => {});
  }, [user]);

  const load = async (tid) => {
    if (!tid) return;
    setLoading(true);
    try {
      const res = await api.get("/performance/kpi", {
        params: { technician_id: tid, date_from: dateFrom, date_to: dateTo },
      });
      setData(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (technicianId) load(technicianId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [technicianId, dateFrom, dateTo]);

  const chartData = useMemo(() => {
    if (!data?.daily) return [];
    return data.daily.map((r) => ({
      date: r.date,
      [t("auto.chart_assegnati")]: r.assigned,
      [t("auto.chart_chiusi")]: r.closed,
      KPI: r.kpi ?? 0,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, t]);

  if (!user) return null;
  if (user.role === "tecnico") return <Navigate to="/dashboard" replace />;

  const totals = data?.totals || { assigned: 0, closed: 0, kpi: null };

  return (
    <div className="space-y-6" data-testid="performance-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{t("auto.kpi_tecnici")}</div>
          <h1 className="mt-1 text-3xl font-bold text-slate-900 lg:text-4xl">{t("menu.performance")}</h1>
          <p className="mt-1 text-sm text-slate-500">{t("auto.assegnati_vs_chiusi_positivi_per_tecnico_e_giorno")}</p>
        </div>
      </div>

      <div className="rounded-sm border border-slate-200 bg-white p-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(220px,1fr)_180px_180px]">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("auto.tecnico")}</Label>
            <Select value={technicianId} onValueChange={setTechnicianId}>
              <SelectTrigger className="mt-1 rounded-sm" data-testid="perf-tech-select"><SelectValue placeholder={t("auto.seleziona_tecnico")} /></SelectTrigger>
              <SelectContent>
                {technicians.map((tech) => <SelectItem key={tech.id} value={tech.id}>{tech.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("auto.dal")}</Label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="mt-1 rounded-sm" data-testid="perf-date-from" />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("auto.al")}</Label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="mt-1 rounded-sm" data-testid="perf-date-to" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="rounded-sm border border-slate-200 bg-white p-5" data-testid="perf-total-assigned">
          <div className="flex items-center justify-between">
            <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{t("auto.totale_assegnati")}</div>
            <Target className="h-4 w-4 text-slate-500" />
          </div>
          <div className="mt-3 font-mono text-4xl font-semibold text-slate-900">{totals.assigned}</div>
        </div>
        <div className="rounded-sm border border-slate-200 bg-white p-5" data-testid="perf-total-closed">
          <div className="flex items-center justify-between">
            <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{t("auto.chiusi_positivi_kicker")}</div>
            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
          </div>
          <div className="mt-3 font-mono text-4xl font-semibold text-emerald-700">{totals.closed}</div>
        </div>
        <div className="rounded-sm border border-slate-200 bg-white p-5" data-testid="perf-total-kpi">
          <div className="flex items-center justify-between">
            <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{t("auto.kpi_complessivo_kicker")}</div>
            <TrendingUp className="h-4 w-4 text-[#0052FF]" />
          </div>
          <div className={`mt-3 inline-block rounded-sm border px-3 py-1 font-mono text-3xl font-semibold ${kpiTone(totals.kpi)}`}>
            {totals.kpi === null || totals.kpi === undefined ? "—" : `${totals.kpi}%`}
          </div>
        </div>
      </div>

      <div className="rounded-sm border border-slate-200 bg-white p-5">
        <div className="mb-3 flex items-center gap-2">
          <Activity className="h-4 w-4 text-[#0052FF]" />
          <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{t("auto.andamento_giornaliero_kicker")}</div>
        </div>
        {chartData.length === 0 ? (
          <div className="rounded-sm border border-dashed border-slate-300 p-8 text-center text-sm text-slate-400" data-testid="perf-chart-empty">
            {loading ? t("auto.caricamento") : t("auto.nessun_dato_periodo")}
          </div>
        ) : (
          <div className="h-72 w-full" data-testid="perf-chart">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="left" tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} domain={[0, 100]} unit="%" />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <ReBar yAxisId="left" dataKey={t("auto.chart_assegnati")} fill="#0052FF" radius={[2, 2, 0, 0]} />
                <ReBar yAxisId="left" dataKey={t("auto.chart_chiusi")} fill="#10b981" radius={[2, 2, 0, 0]} />
                <Line yAxisId="right" type="monotone" dataKey="KPI" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="rounded-sm border border-slate-200 bg-white">
        <div className="border-b border-slate-200 p-4 text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{t("auto.dettaglio_giornaliero_kicker")}</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="perf-daily-table">
            <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">
              <tr>
                <th className="p-3">{t("common.date")}</th>
                <th className="p-3 text-right">{t("auto.col_assegnati")}</th>
                <th className="p-3 text-right">{t("auto.col_chiusi_positivi")}</th>
                <th className="p-3 text-right">KPI</th>
              </tr>
            </thead>
            <tbody>
              {(data?.daily || []).length === 0 && (
                <tr><td colSpan={4} className="p-6 text-center text-slate-400">{t("auto.nessun_dato")}</td></tr>
              )}
              {(data?.daily || []).map((r) => (
                <tr key={r.date} className="border-t border-slate-100" data-testid={`perf-row-${r.date}`}>
                  <td className="p-3 font-mono">{r.date}</td>
                  <td className="p-3 text-right font-mono">{r.assigned}</td>
                  <td className="p-3 text-right font-mono text-emerald-700">{r.closed}</td>
                  <td className="p-3 text-right">
                    <span className={`inline-block rounded-sm border px-2 py-0.5 font-mono text-xs font-semibold ${kpiTone(r.kpi)}`}>
                      {r.kpi === null || r.kpi === undefined ? "—" : `${r.kpi}%`}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
